import { getParameterByName } from '@qdabra/forms-shared/urlHelpers';
import { prop } from 'ramda';
import {
    getMainDom,
    standardNamespaces,
} from '../dataConnections/dataConnection.utils';
import defaultValueApplicator from './DataSources/defaultValueApplicator';
import fieldChangeListener from './DataSources/fieldChangeListener';
import fieldChangeRuleExecutor from './DataSources/fieldChangeRuleExecutor';
import buildRuleSetExecutor from './Rules/buildRuleSetExecutor';
import makeEnvironmentService from './Services/environmentService';
import failureDialog from './UI/failureDialog';
import showLoadFailedError from './UI/loadFailedError';
import { showMessageBox } from './UI/messageBox';
import createCustomCodeApi from './customCodeModel';
import fileDropHandler from './fileDropHandler';
import makePeopleSearcher from './peopleSearches/peopleSearcher';
import * as commands from './qRules/commands';
import qRules from './qRules/core/qRules';
import sampleDataSource from './sampleDataSource';
import { hostWebUrl } from './sharePoint/sharePointHelper';
import validation, { VALIDATION_MODE_AUTO } from './validation';
import viewActionHandler from './viewActionHandler';
import viewWatcher from './viewWatcher';

const { formsViewer, qd } = window;

const { preloadData } = qd.formsViewer;

function showQueryErrorMessage(err, errMsg) {
    var errDetails = '';

    console.error(err);

    if (err) {
        if (err.shpError && err.shpError.get_message) {
            errDetails = err.shpError.get_message();
        } else if (err.message) {
            errDetails = err.message;
        }
    }

    var fd = new failureDialog('Query Failed', errMsg, errDetails);

    return fd.showAsync();
}

const queryOnLoadDataConnection = async (dataConnections, name) => {
    try {
        const conn = dataConnections.get(name);

        await conn.queryAsync();
    } catch (err) {
        const errMsg = `An error occurred querying the data connection ${name}.`;

        await showQueryErrorMessage(err, errMsg);
    }
};

const engine = function (
    viewManager,
    template,
    functions,
    renderTarget,
    shpAccess,
    xpathEngine,
    dataConnections,
    dataSources,
    qfsAccess,
    mainXml,
    characterLimitSource,
    showLoader,
    isNewDocument,
    logging,
    settings,
) {
    const defValues = defaultValueApplicator(template, dataSources);

    const sampleDataSourceInst = sampleDataSource(template, xpathEngine);

    const validationInst = validation(dataSources, template);

    const peopleSearcher = makePeopleSearcher(shpAccess, hostWebUrl);

    const ruleSetExecutor = buildRuleSetExecutor(
        dataConnections,
        viewManager,
        logging,
    );

    const environment = makeEnvironmentService(
        preloadData.userName,
        getParameterByName,
        window,
    );

    function renderViewAsync() {
        if (validationInst.getMode() === VALIDATION_MODE_AUTO) {
            validationInst.validateAll();
        }

        return viewManager.startRenderViewAsync(
            getMainDom(dataSources),
            functions,
        );
    }

    // Get a dataSourceNode for the main data source's document element
    function getDocumentElement() {
        return dataSources.getMainDocumentElement();
    }

    /**
     * Run the specified ruleSet
     * @param {RuleSet} ruleSet - A rule set definition
     * @param {DataSourceNode} context - a dataSourceNode object. if unspecified, the document element of the main data source will be used as the context
     * @returns {Promise} Promise that resolves when this rule set and all ensuing actions have run
     */
    function runRuleSetAsync(ruleSet, context) {
        return ruleSetExecutor.runRuleSetAsync(
            ruleSet,
            context || getDocumentElement(),
        );
    }

    // Error -> Promise
    function unhandledError(error) {
        const message =
            (error && error.userDisplayMessage) ||
            'An unexpected error occurred.\n\nSee browser console for details or contact technical support.';

        logging.logError(`Unhandled error: ${message}`, error);

        return showMessageBox('Error', message);
    }

    const fileDropHandlerInst = fileDropHandler(
        dataSources,
        sampleDataSourceInst,
        renderViewAsync,
    );

    function makeqRulesInstance() {
        return qRules({
            dataConnections,
            dataSources,
            template,
            qfsAccess,
            xpathEngine,
            shpAccess,
            viewManager,
            sampleDataSource: sampleDataSourceInst,
            settings: {
                user: preloadData.userName,
                functions,
            },
            characterLimitSource,
            logging,
            services: {
                settings,
                fileDrop: fileDropHandlerInst,
                validation: validationInst,
                environment,
                peopleSearch: peopleSearcher,
            },
        });
    }

    // Must be run after anything this function references is created
    const qRulesInst = makeqRulesInstance();

    const { runViewEvent } = viewActionHandler(
        qfsAccess,
        shpAccess,
        hostWebUrl,
        validationInst,
        renderViewAsync,
        runRuleSetAsync,
        template,
        viewManager,
        showLoader,
        renderTarget,
        dataConnections,
        dataSources,
        unhandledError,
        fileDropHandlerInst,
        peopleSearcher,
    );

    // 2022-10-08 - Removed unused handleQueryError()

    // EventInfo -> Promise
    const handleViewEvent = (eventInfo) =>
        runViewEvent(eventInfo).catch(unhandledError);

    const initDataSourceAsync = async (dataConnection) => {
        try {
            await dataConnection.initAsync();
        } catch (e) {
            const errTitle = `Error initializing ${dataConnection.name}`;
            const fd = new failureDialog(
                errTitle,
                e.message,
                e.description || '',
            );

            await fd.showAsync();
        }
    };

    // void -> Promise
    const initDataSourcesAsync = async () => {
        await Promise.all(dataConnections.getAll().map(initDataSourceAsync));
    };

    // void -> Promise[void]
    const queryOnLoadDataConnections = async () => {
        const onLoadDses = dataSources.getAll().filter(prop('initOnLoad'));

        for (const dSource of onLoadDses) {
            await queryOnLoadDataConnection(dataConnections, dSource.name);
        }
    };

    function addViewEvents() {
        const vw = viewWatcher(renderTarget);

        vw.subscribe(handleViewEvent);
    }

    function handleInitError(e) {
        logging.logError('Error during initialization', e);

        // intentionally not returning so that wait overlay can hide
        showLoadFailedError(e, renderTarget);
    }

    async function processInitialDefaultValuesAsync() {
        // Case 50821 - Only re-evalue default values on new documents
        if (isNewDocument) {
            logging.logVerbose('Begin processing initial default values');

            await defValues.processAllDefaultValues();

            logging.logVerbose('End processing initial default values');
        }
    }

    async function performFormLoadRulesAsync() {
        const loadRules = template.getOnLoadRules();

        if (loadRules) {
            // Context node for on load rules is the main DOM's document element
            await runRuleSetAsync(loadRules);
        }
    }

    function initFieldChangeListener() {
        const executor = fieldChangeRuleExecutor(template, ruleSetExecutor);
        const listener = fieldChangeListener(defValues, executor, qRulesInst);

        dataSources.setListener(listener);
    }

    async function initQRulesAsync() {
        for (const [name, definition] of Object.entries(commands)) {
            qRulesInst.addCommand(name, definition);
        }

        await qRulesInst.initializeAsync();
    }

    const setQueryListItemId = async (listItemId) => {
        const namespaces = standardNamespaces;
        const dataSource = dataSources.getDom('');
        const idField =
            dataSource &&
            dataSource.selectSingle(
                '/*/dfs:queryFields/listq:SharePointListItem_RW/listq:ID',
                { namespaces },
            );

        if (!idField) {
            console.warn('Unable to locate ID query field in main data source');
            return;
        }

        await idField.setValueAsync(listItemId);
    };

    // () -> Promise
    const checkAndQueryListFormAsync = async () => {
        const getParam = getParameterByName;
        const listItemId = getParam('listItemId') || getParam('FVListItemId');
        const skipLoad = getParam('FVSkipListItemLoad') === 'true';
        const listItemIdInt = parseInt(listItemId);

        if (Number.isNaN(listItemIdInt) || listItemIdInt < 1) {
            return;
        }

        const mainQuery = dataConnections.getMainQuery();

        const isListForm = mainQuery && mainQuery.type === 'shpListForm';

        if (!isListForm) {
            return;
        }

        if (skipLoad) {
            await setQueryListItemId(listItemId);

            return;
        }

        await mainQuery.queryByIdAsync(listItemIdInt);
    };

    function registerCustomCodeApi() {
        formsViewer.registerInstance(
            createCustomCodeApi(
                dataConnections,
                dataSources,
                viewManager,
                qRulesInst,
                renderViewAsync,
            ),
        );
    }

    async function initAsync() {
        const initActions = [
            initFieldChangeListener,
            registerCustomCodeApi,
            initDataSourcesAsync,
            checkAndQueryListFormAsync,
            queryOnLoadDataConnections,
            addViewEvents,
            processInitialDefaultValuesAsync,
            initQRulesAsync,
            performFormLoadRulesAsync,
            renderViewAsync,
        ];

        try {
            for (const action of initActions) {
                await action();
            }
        } catch (e) {
            handleInitError(e);
        }
    }

    return {
        initAsync,
    };
};

export default engine;
